
import ffs, { Ffs } from "kyofuuc";
import { CacheManager } from "../utils";
import { NoseurObject, TypeChecker, Toaster, Scheme, MessageSchemesIcons } from "@ronuse/noseur";

export interface ApiResponseData<T> {
    data?: T;
    content?: T[];
    size?: number;
    empty?: boolean;
    transformed?: any;
    total_pages?: number;
    total_elements?: number;
    number_of_elements?: number;
    pageable?: {
        paged: boolean;
        offset: number;
        page_size: number;
        page_number: number;
    };
};

export interface ApiResponse<T> {
    message: string;
    success: boolean;
    data?: ApiResponseData<T>;
    secondary_data: NoseurObject<any>;
};

export type SanitizedResponse<T> = {
    sanitized: T;
    status: number;
    errors?: any[];
    errorMessage: string;
    apiResponse: ApiResponse<T>;
};

export enum Level {
    INFO = "INFO",
	WARN = "WARN",
	ERROR = "ERROR",
	TRACE = "TRACE",
	FATAL = "FATAL",
	PANIC = "PANIC",
	VERBOSE = "VERBOSE",
}

export interface ILog {
    id: number;
    level: Level;
    group: string;
    source: string;
    content: string;
    span_id: string;
    trace_id: string;
    created_at: string;
}

export class MnemosyneService {

    // remove /json route after add formData submission on kyofuuc, and remove the headers this.config
    config: any = {
        headers: {
            Authorization: this.getAccessToken()
        }
    };
    transport: Ffs;
    baseUrl: string = process.env.REACT_APP_MNEMOSYNE_BASE_API_URL ?? "http://172.20.10.4:9302";

    protected static instance: MnemosyneService;

    constructor(baseUrl?: string) {
        this.transport = ffs.init({
            cache: false,
            promiscuous: true,
            baseUrl: baseUrl ?? this.baseUrl,
            ...this.config
        } as any);
    }

    static getInstance() {
        if (!MnemosyneService.instance) MnemosyneService.instance = new MnemosyneService();
        return MnemosyneService.instance;
    }

    getAccessToken() {
        const accessToken = CacheManager.get(CacheManager.ACCESS_TOKEN_KEY);
        if (!accessToken) return null;
        return `Bearer ${accessToken}`;
    }

    normalizeParams(params: NoseurObject<any>) {
        if (!params.page) params.page = 1;
        if (!params.size) params.size = 10;
        if (!params.sort) params.sort = "id,DESC";
        Object.keys(params).forEach((key: string) => {
            const value = params[key];
            if (!TypeChecker.isArray(value)) return;
            params[key] = value.reduce((acc: string, v: string, index: number) => {
                if (index > 0) return `${acc},${key}=${v}`;
                return `${v}`;
            }, "");
        });
        return params;
    }

    report<T>(promise: Promise<any>, postResolve?: Function, postReject?: Function): Promise<SanitizedResponse<any>> {
        return new Promise((resolve, reject) => {
            promise.then((response: T) => {
                let transformedRespomse = this.buildResponse(response);
                if (postResolve) postResolve(transformedRespomse);
                resolve(transformedRespomse);
            }).catch((error: any) => {
                let transformedError = this.buildError(error);
                if (postReject) postReject(transformedError);
                reject(transformedError);
                if (error.response?.status === 401) {
                    setTimeout(() => this.backToLogin(), 5000);
                }
            });
        });
    }

    buildResponse<T>(response: any): SanitizedResponse<T> {
        if (!response.data || !response.data.data) return response;
        response.status = response?.status;
        response.apiResponse = response.data;
        response.sanitized = response.apiResponse.data;
        return response;
    }

    buildError<T>(error: any): SanitizedResponse<T> {
        if (!error) error = {};
        error.message = error?.response?.data?.message ?? error?.response?.message ?? error?.message ?? "An error occur please try again later";
        error.errors = error?.response?.data?.errors;
        error.errorMessage = error.message;
        error.status = error.response?.status ?? -1;
        return error;
    }

    backToLogin() {
        CacheManager.clear();
        window.location = "/?loc=" + encodeURIComponent(window.location.pathname + window.location.search) as any;
    }

    queryLogs(params: NoseurObject<string | number> = { page: 1, size: 1000 }): Promise<any> {
        return this.report(this.transport.get("/mnemosyne/api/v1/log", { query: this.normalizeParams(params) } as any));
    }

    static reportError<T>(response: SanitizedResponse<T>) {
        Toaster.toast({
            lifetime: 5000,
            foreScheme: true,
            scheme: Scheme.DANGER,
            showProgressbar: true,
            pauseDelayOnHover: true,
            style: { background: "white" },
            icon: MessageSchemesIcons.INFO,
            content: response?.errorMessage ?? `${response}`,
        });
    }

}
